import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { useCallback } from "react"
import { connect, useSelector } from "react-redux"

import { updateChoice } from "areas/product/store/actions"
import {
  ChoiceOption,
  OptionTitle,
  OptionPrice,
} from "areas/product/wizard-options/components"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselScrollBar,
} from "components/ui/carousel"
import { isTestMode } from "helpers/application"
import { to_currency } from "helpers/number"
import displayOptionDescription from "helpers/product/display-option-description"
import displayOptionPrice from "helpers/product/display-option-price"
import { useNavigation } from "helpers/use-navigation"

import MetalIcon from "./choice/metal-icon"

const MetalOptions = ({ data, selectedValue, setChoice, surcharge }) => {
  const pdpExposedMetalExp = useSelector(
    (state) => state.experiment.pdpExposedMetal.variant_id
  )
  const keyboardProps = useNavigation({ direction: "both" })

  if (pdpExposedMetalExp === 2) {
    const handleArrowClick = useCallback(() => {
      if (!isTestMode) {
        dataLayer.push({ event: "clicked-carousel-arrow" })
      }
    }, [])

    const handleScrollBarChange = useCallback(() => {
      if (!isTestMode) {
        dataLayer.push({ event: "clicked-carousel-scroll-bar" })
      }
    }, [])

    return (
      <Carousel
        disableSkip
        opts={{
          dragFree: true,
          loop: false,
          slidesToScroll: "auto",
        }}
      >
        <CarouselContent
          aria-label={`${data.heading.title} Options`}
          className="-ml-10 pb-12"
          role="radiogroup"
        >
          {data.options.map((metal) => {
            const selected = selectedValue === metal.value

            return (
              <CarouselItem
                className="tablet:basis-[28%] max-w-112 tablet:max-w-none basis-[30%] pl-10"
                key={`metal-${metal.value}`}
                role="none"
              >
                <ChoiceOption
                  aria-checked={selected}
                  className="tablet:mb-12 mb-0 flex-col p-4 pb-8"
                  data-cy={`product-wizard-option-sku-${metal.value}`}
                  data-is-option="true"
                  data-is-selected={selected}
                  onClick={() => setChoice(data.pref, metal)}
                  role="radio"
                  tabIndex={selected ? 0 : -1}
                  {...keyboardProps}
                >
                  <MetalIcon metal={metal} />
                  <OptionTitle className="line-clamp-2 text-center">
                    {metal.title}
                  </OptionTitle>
                  <OptionPrice>
                    {to_currency(metal.display_price + surcharge)}
                  </OptionPrice>
                </ChoiceOption>
              </CarouselItem>
            )
          })}
        </CarouselContent>
        <div className="flex items-center justify-between gap-10">
          <CarouselScrollBar onChange={handleScrollBarChange} />
          <div className="flex items-center gap-8">
            <CarouselPrevious
              alwaysShow
              arrowClassName="text-14"
              className="translate-none relative left-auto top-auto size-24"
              onClick={handleArrowClick}
            />
            <CarouselNext
              alwaysShow
              arrowClassName="text-14"
              className="translate-none relative right-auto top-auto size-24"
              onClick={handleArrowClick}
            />
          </div>
        </div>
      </Carousel>
    )
  }

  return (
    <div
      aria-label={`${data.heading.title} Options`}
      className="tablet:px-6 desktop:grid-cols-4 tablet:gap-6 gap-18 grid auto-rows-fr grid-cols-3"
      role="radiogroup"
    >
      {data.options.map((metal) => {
        const selected = selectedValue === metal.value
        return (
          <ChoiceOption
            aria-checked={selected}
            className="tablet:mb-12 mb-0 flex-col border-2 p-4 pb-8"
            data-cy={`product-wizard-option-sku-${metal.value}`}
            data-is-option="true"
            data-is-selected={selected}
            key={`metal-${metal.value}`}
            onClick={() => setChoice(data.pref, metal)}
            role="radio"
            tabIndex={selected ? 0 : -1}
            {...keyboardProps}
          >
            <MetalIcon metal={metal} />
            <OptionTitle className="line-clamp-2 text-center">
              {metal.title}
            </OptionTitle>
            <OptionPrice>
              {to_currency(metal.display_price + surcharge)}
            </OptionPrice>
          </ChoiceOption>
        )
      })}
    </div>
  )
}

MetalOptions.propTypes = {
  data: PropTypes.object,
  selectedValue: PropTypes.string,
  setChoice: PropTypes.func,
  surcharge: PropTypes.number,
}

const InternalMetalSelectionStatic = ({
  data,
  description,
  headingTitle,
  hidePrice,
  price,
  selectedValue,
  setChoice,
  surcharge,
}) => {
  if (!data || data.disabled) return null

  return (
    <div data-cy={`wizard-accordion-${data.pref}`}>
      <div className="tablet:px-6 bg-white py-4">
        <div className="tablet:pr-6 flex justify-between py-6">
          <div className="flex">
            <div className="flex flex-col items-start">
              <h2 className="text-14 tablet:font-bold tablet:tracking-half m-0 font-semibold uppercase text-black">
                Choose Your {headingTitle}
              </h2>
              <p className="text-grey-35 text-12 tablet:text-grey-65 tablet:font-normal tablet:tracking-half m-0 font-semibold">
                {description}
              </p>
            </div>
          </div>

          {!hidePrice && (
            <p
              className="text-14 tracking-half flex items-center font-bold"
              data-ge-parentproduct-base-price
            >
              {price}
            </p>
          )}
        </div>
      </div>

      <div className="pb-16 pt-10">
        <FocusScope>
          <MetalOptions
            data={data}
            selectedValue={selectedValue}
            setChoice={setChoice}
            surcharge={surcharge}
          />
        </FocusScope>
      </div>
    </div>
  )
}

InternalMetalSelectionStatic.propTypes = {
  data: PropTypes.object,
  description: PropTypes.string,
  headingTitle: PropTypes.string,
  hidePrice: PropTypes.bool,
  price: PropTypes.string,
  selectedValue: PropTypes.string,
  setChoice: PropTypes.func,
  surcharge: PropTypes.number,
}

const mapStateToProps = (state, ownProps) => {
  const data = state.wizard.data[ownProps.pref]
  const selected = state.choices[ownProps.pref]
  const metalsIncludeSurchargeFor = state.wizard.metalsIncludeSurchargeFor

  const headingTitle = data?.heading?.title || ""

  const surcharge = metalsIncludeSurchargeFor.reduce((accu, curr) => {
    let surchargeFor = state.choices[curr]
    return accu + (surchargeFor?.display_price || surchargeFor?.price || 0)
  }, 0)

  return {
    data,
    description: displayOptionDescription(data, state, ownProps),
    headingTitle,
    hidePrice: metalsIncludeSurchargeFor.includes(ownProps.pref),
    price: displayOptionPrice(state, ownProps),
    selectedValue: selected?.value || "",
    surcharge,
  }
}

const mapDispatchToProps = (dispatch) => ({
  setChoice: (pref, metal) => {
    if (!isTestMode) {
      dataLayer.push({
        event: "selected-metal-option",
      })
    }
    dispatch(updateChoice(pref, metal, true))
  },
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalMetalSelectionStatic)
